import type { UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";

import type {
  BalanceBillAttachmentRequest,
  BalanceBillItemRequest,
  BalanceBillItemResponse,
  BalanceBillListRequest,
} from "@/model/balance-bill";
import {
  fetchSingleBalanceBill,
  fetchBalanceBillList,
  uploadBalanceBill,
  fetchSingleBalanceBillAttachment,
} from "@/services/balance-bill-service";
import type { BalanceBillListResponseDataType } from "@/types";
import useUserInfo from "./use-user-info";
import { QueryKeys } from "@/constants";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useNotificationError } from "@/context/error-provider";
import { getErrorMessage } from "@/lib/error-message-utils";
import { ErrorNotificationType } from "@/pages/components/error-notification";

export const useBalanceBillList = (): UseQueryResult<
  BalanceBillListResponseDataType[],
  Error
> => {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const balanceBillListPayload: BalanceBillListRequest = {
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    planId: activeCoverage?.planId,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
    memberId: userInfo?.memberId,
    token: userInfo?.accessToken,
  };

  return useQuery({
    queryKey: [
      QueryKeys.BALANCE_BILL_LIST,
      balanceBillListPayload?.claimCoverageCode,
      balanceBillListPayload?.planId,
    ],
    queryFn: async () => {
      const balanceBillListResponse = await fetchBalanceBillList(
        balanceBillListPayload,
      );
      return balanceBillListResponse?.data;
    },
    staleTime: Infinity,
    retry: false,
    retryOnMount: false,
    enabled: Boolean(balanceBillListPayload?.token),
  });
};

export const useBalanceBillUpload = () => {
  return useMutation({
    mutationFn: uploadBalanceBill,
  });
};

export const useBalanceBillItemDetails = () => {
  const { addError } = useNotificationError();
  return useMutation({
    mutationFn: async (balanceBillItemRequest: BalanceBillItemRequest) => {
      const balanceBillResponse: BalanceBillItemResponse =
        await fetchSingleBalanceBill(balanceBillItemRequest);
      return balanceBillResponse?.data;
    },
    onError: (err) => {
      addError({
        message: getErrorMessage(err),
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

export const useAttachmentDetail = ({
  attachmentId,
  ticketId,
}: {
  attachmentId: string;
  ticketId: string;
}) => {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const balanceBillAttachmentRequest: BalanceBillAttachmentRequest = {
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    planId: activeCoverage?.planId,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
    memberId: userInfo?.memberId,
    attachmentId,
    ticketId,
    token: userInfo?.accessToken,
  };
  return useQuery({
    queryKey: [
      QueryKeys.BALANCE_BILL_SINGLE_ATTACHMENT,
      balanceBillAttachmentRequest.attachmentId,
      balanceBillAttachmentRequest.claimCoverageCode,
      balanceBillAttachmentRequest.planId,
    ],
    queryFn: async () => {
      const balanceBillAttachmentResponse =
        await fetchSingleBalanceBillAttachment(balanceBillAttachmentRequest);
      return balanceBillAttachmentResponse;
    },
    gcTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(
      Boolean(balanceBillAttachmentRequest?.attachmentId) &&
      Boolean(balanceBillAttachmentRequest?.ticketId),
    ),
  });
};
